import { useState } from "react";
import { Link, useParams } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { useAction } from "../../../components/ui";
import { useVertragDaten } from "./useVertrag";
import { VertragPapier, marksOf } from "./Papier";
import { contractNumberLabel, statusPill } from "./modell";
import { PROTOCOL_TEXT, protocolFromEvents, protocolRows, stamp, type Fassung } from "./protokoll";
import { ausfertigungAusDetail, ausfertigungFileName, ausfertigungPdf, saveFile } from "./ausfertigung";
import { t } from "../../../lib/sprache";
import { geltendeVorlage } from "../../../model/beleg-vorlagen";

export { PROTOCOL_TEXT } from "./protokoll";

/**
 * Druckansicht je Fassung (Befund 9): Agentur mit Anlage, Marke und Creator ohne die vor ihnen verborgene Anlage; Protokoll
 * ohne E-Mail- und IP-Adressen (Befund 20). PDF per Klick oder über den Druckdialog; gedruckt wird als Fließtext oder, auf
 * Wunsch, in denselben Seiten wie das PDF (K-513).
 */
export function VertragDruck() {
  const { contractId = "" } = useParams();
  const ws = useStaffWorkspace();
  const { detail, org } = useVertragDaten(ws, contractId);
  const [view, setView] = useState<Fassung>("agency");
  const [seiten, setSeiten] = useState(false);
  const { busy, error, run } = useAction();
  if (detail.error || org.error) return <ErrorState error={detail.error ?? org.error} />;
  if (detail.isPending || org.isPending) return <Loading />;
  const d = detail.data;
  if (!d) return <NoAccess text={t("Diesen Vertrag gibt es nicht oder er ist für dein Konto nicht freigegeben.", "This contract does not exist or is not available to your account.")} />;
  const c = d.contract;
  const tz = ws.org.timezone;
  const lang = c.language;
  const tl = PROTOCOL_TEXT[lang];
  const number = contractNumberLabel(c.contract_number, c.contract_number_label);
  const agencyName = org.data.settings.legal_name ?? ws.org.name;
  const hidden = view !== "agency" && c.annex_hidden_from.includes(view);
  const views: Fassung[] = ["agency", ...(c.required_signers.includes("brand") ? ["brand" as const] : []), ...(c.talent_id || c.required_signers.includes("talent") ? ["talent" as const] : [])];
  const protokoll = protocolRows(protocolFromEvents(d.events, d.signers), lang, tz, view);
  return (
    <main id="main" className="content stack">
      <div className="row between tk-noprint">
        <Link className="btn" to={`/agentur/vertraege/${c.id}`}>{t("Zurück zum Vertrag", "Back to the contract")}</Link>
        <span role="group" aria-label={t("Fassung für", "Version for")} className="row">
          {views.map((v) => <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>{tl.fassung[v]}</button>)}
        </span>
        <span role="group" aria-label={t("Drucken als", "Print as")} className="row">
          <button type="button" aria-pressed={!seiten} onClick={() => setSeiten(false)}>{t("Fließtext", "Continuous text")}</button>
          <button type="button" aria-pressed={seiten} onClick={() => setSeiten(true)}>{t("Seiten wie im PDF", "Pages as in the PDF")}</button>
        </span>
        <span className="row">
          <button type="button" disabled={!!busy} onClick={() => void run("pdf", async () => {
            saveFile(await ausfertigungPdf({ ...ausfertigungAusDetail(d, view, agencyName, tz), logoPath: ws.org.logo_path, accent: ws.org.brand_color,
              layout: await geltendeVorlage(ws.org.org_id, "contract", c.brand_id ?? null) }), ausfertigungFileName(c.title, c.contract_number_label, view, lang));
            return true;
          })}>{busy ? t("Wird erstellt …", "Creating …") : t("PDF herunterladen", "Download PDF")}</button>
          <button className="primary" type="button" onClick={() => window.print()}>{t("Drucken", "Print")}</button>
        </span>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
      {hidden && <p className="notice tk-noprint">{t("Diese Fassung enthält den vertraulichen Teil nicht, weil er vor ", "This version does not contain the confidential part because it is hidden from ")}{tl.roles[view as "brand" | "talent"]} {t("verborgen ist.", ".")}</p>}
      <VertragPapier nested={false} seiten={seiten} logoPath={ws.org.logo_path} accent={ws.org.brand_color} html={c.rendered_html} annexHtml={hidden ? null : c.rendered_annex_html} defs={c.variable_defs} timeZone={tz} lang={lang}
        eyebrow={`${agencyName} · ${number} · ${tl.fassung[view]}${!hidden && c.rendered_annex_html?.trim() ? tl.withAnnex : ""}${lang === "de" ? ` · ${statusPill(c).label}` : ""}`}
        marks={marksOf(d.signers)} />
      <article className="paper tk-print-page tk-protocol" lang={lang}>
        <h2>{tl.title}</h2>
        <p>{tl.intro(c.title, number, stamp(c.sent_at, tz, lang), c.completed_at ? stamp(c.completed_at, tz, lang) : null)}</p>
        {c.document_sha256 && <p className="tk-mono">{tl.atSend}: {c.document_sha256}</p>}
        {c.final_sha256 && <p className="tk-mono">{tl.atEnd}: {c.final_sha256}</p>}
        <div className="tablewrap">
          <table>
            <caption className="sr-only">{tl.title}</caption>
            <thead><tr>{tl.cols.map((h, i) => <th key={h} scope="col">{i === 0 ? `${h} (${tz})` : h}</th>)}</tr></thead>
            <tbody>
              {protokoll.map((r) => (
                <tr key={r.id}><td>{r.at}</td><td>{r.event}</td><td>{r.person}</td><td className="tk-mono">{r.evidence}</td></tr>
              ))}
              {/* UI-A1: ohne Ereignisse eine erklärende Zeile statt einer leeren Tabelle */}
              {!protokoll.length && <tr><td colSpan={4} className="muted">{lang === "en" ? "No signature yet" : "Noch keine Unterschrift"}</td></tr>}
            </tbody>
          </table>
        </div>
        <h3 className="tk-protocol-how">{tl.how}</h3>
        <p>{tl.howText}</p>
      </article>
    </main>
  );
}
